import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getCurrencyByRegion } from '@principle-theorem/accounting';
import {
  CurrentBrandScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  confirmationDialogData,
  type IBreadcrumb,
  type IConfirmationDialogData,
} from '@principle-theorem/ng-shared';
import {
  AutomationTemplate,
  Brand,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  AutomationTemplateKey,
  type IBrand,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  filterUndefined,
  type WithRef,
} from '@principle-theorem/shared';
import { of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { type DepositSettingsFormComponent } from '../../practices/patient-portal/deposit-settings-form/deposit-settings-form.component';
import { DepositSettingsSaveError } from './deposit-settings-save-error';

@Component({
  selector: 'pr-deposit-settings',
  templateUrl: './deposit-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DepositSettingsComponent {
  private _brandScope = inject(CurrentBrandScope);
  private _organisation = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);
  private _basicDialog = inject(BasicDialogService);
  private _brand$: Observable<WithRef<IBrand>> =
    this._brandScope.doc$.pipe(filterUndefined());
  private _brand = toSignal(this._brand$);
  private _organisationSignal = toSignal(
    this._organisation.organisation$.pipe(filterUndefined())
  );
  private _depositReminderAutomationTemplate =
    AutomationTemplate.getTemplates().find(
      (template) => template.key === AutomationTemplateKey.DepositReminderEmail
    );

  saving = signal(false);
  brand$: Observable<WithRef<IBrand>> = this._brand$;
  breadcrumbs$: Observable<IBreadcrumb[]> = this.brand$.pipe(
    map((brand) => [
      { label: 'Settings', path: '../../../' },
      { label: brand.name },
      { label: 'Deposit Settings' },
    ])
  );
  depositSettings = computed(
    () => this._brand()?.settings.onlineBookings?.deposit
  );
  brandUid = computed(() => this._brand()?.ref.id);
  currency = computed(() => {
    const organisation = this._organisationSignal();
    return organisation ? getCurrencyByRegion(organisation.region) : undefined;
  });
  schedulingEventReasons = toSignal(
    this._brand$.pipe(switchMap((brand) => Brand.cancellationReasons$(brand))),
    { initialValue: [] }
  );
  treatmentTemplates = toSignal(
    this._brand$.pipe(switchMap((brand) => Brand.treatmentTemplates(brand))),
    { initialValue: [] }
  );
  treatmentConfigurations = toSignal(
    this._brand$.pipe(switchMap((brand) => TreatmentConfiguration.all$(brand))),
    { initialValue: [] }
  );
  hasReminderAutomation = toSignal(
    this._brand$.pipe(
      switchMap((brand) => {
        const template = this._depositReminderAutomationTemplate;
        return template
          ? AutomationTemplate.getExistingConfigurations$(template, brand).pipe(
              map((configs) => configs.length > 0)
            )
          : of(false);
      })
    ),
    { initialValue: false }
  );

  async saveDepositSettings(
    depositSettingsForm: Pick<
      DepositSettingsFormComponent,
      'valid' | 'getRawValue' | 'markAsPristine' | 'treatmentTemplateName'
    >
  ): Promise<void> {
    if (!depositSettingsForm.valid) {
      return;
    }
    try {
      const formDeposit = depositSettingsForm.getRawValue();
      const brand = this._brand();
      if (!brand) {
        this._snackBar.open('Unable to save: brand not loaded');
        return;
      }
      const deposit = formDeposit;
      const saveError = DepositSettingsSaveError.get(deposit, (templateUid) =>
        depositSettingsForm.treatmentTemplateName(templateUid)
      );
      if (saveError) {
        this._snackBar.open(saveError);
        return;
      }
      await Firestore.updateDoc(brand.ref, {
        settings: {
          ...brand.settings,
          onlineBookings: {
            ...brand.settings?.onlineBookings,
            deposit,
          },
        },
      });
      depositSettingsForm.markAsPristine();
      this._snackBar.open('Deposit settings saved');
    } catch {
      this._snackBar.open('Failed to save deposit settings');
    }
  }

  async createReminderAutomation(): Promise<void> {
    const brand = this._brand();
    if (!brand) {
      return;
    }
    const template = this._depositReminderAutomationTemplate;
    if (!template) {
      return;
    }
    const data: IConfirmationDialogData = confirmationDialogData({
      title: 'Set Up Deposit Reminder Automation',
      prompt:
        'This will create an automated email reminder for patients with an unpaid deposit, sent 48 hours before their appointment.',
      submitLabel: 'Set Up',
    });
    const confirmed = await this._basicDialog.confirm(data);
    if (!confirmed) {
      return;
    }
    this.saving.set(true);
    try {
      await AutomationTemplate.seed(template, brand);
      this._snackBar.open('Deposit reminder automation created');
    } catch {
      this._snackBar.open('Failed to create deposit reminder automation');
    } finally {
      this.saving.set(false);
    }
  }
}
